'use client';

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

import { AnalyticsEvent } from './events';
import { getPostHog } from './posthog-client';

export type WebVitalRating = 'good' | 'needs-improvement' | 'poor';

export interface WebVitalMetric {
  name: string;
  value: number;
  rating: WebVitalRating;
  delta: number;
  id: string;
  timestamp: number;
}

const VALID_RATINGS: ReadonlySet<WebVitalRating> = new Set([
  'good',
  'needs-improvement',
  'poor',
]);

function isValidMetric(metric: WebVitalMetric): boolean {
  if (!metric.name || typeof metric.name !== 'string') return false;
  if (typeof metric.value !== 'number' || !Number.isFinite(metric.value)) {
    return false;
  }
  if (metric.value < 0) return false;
  if (!VALID_RATINGS.has(metric.rating)) return false;
  if (typeof metric.delta !== 'number' || !Number.isFinite(metric.delta)) {
    return false;
  }
  if (!metric.id || typeof metric.id !== 'string') return false;
  if (
    typeof metric.timestamp !== 'number' ||
    !Number.isFinite(metric.timestamp)
  ) {
    return false;
  }
  return true;
}

function dispatchMetric(metric: Metric): void {
  try {
    const client = getPostHog();
    if (!client) return;

    const payload: WebVitalMetric = {
      name: metric.name,
      value: metric.value,
      rating: metric.rating as WebVitalRating,
      delta: metric.delta,
      id: metric.id,
      timestamp: performance.now(),
    };

    if (!isValidMetric(payload)) return;

    client.capture(AnalyticsEvent.WebVitalCollected, { ...payload });
  } catch {
    // Never let a metric callback break the page.
  }
}

// Forward Core Web Vitals to PostHog as `web_vital_collected` events. No-op
// when PostHog is disabled or outside the browser.
export function initWebVitals(): void {
  if (typeof window === 'undefined') return;
  if (!getPostHog()) return;

  try {
    onCLS(dispatchMetric);
    onFCP(dispatchMetric);
    onINP(dispatchMetric);
    onLCP(dispatchMetric);
    onTTFB(dispatchMetric);
  } catch {
    // Silent: telemetry must never break the page.
  }
}
